import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import DownloadIcon from "@mui/icons-material/Download";
import EditIcon from "@mui/icons-material/Edit";
import PriceChangeIcon from "@mui/icons-material/PriceChange";

import styles from "./TradeCallsTable.module.css";
import { Tooltip } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";

import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { axiosConfig } from "../../../../../axios/axiosConfig";
import toast from "react-hot-toast";
import usePartnersWS from "../../../../../hooks/usePartnersWS";
import { partnersWSManager } from "../../../../../services/partnersWSManager";
import { calcLivePnl } from "../../../../../utils/livePnl";
import { isFuturesType, getClosedTradesUrl } from "../tradeTypes";

const fmtDate = (val) => {
  if (!val) return "—";
  const d = new Date(val);
  if (isNaN(d)) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}-${mm}-${yyyy} || ${hh}:${min}`;
};

// % move from entry to the high touched after entry — direction-aware (Sell flips the sign)
const calcHighAfterEntryPct = (entryPrice, asPerHigh, signal) => {
  const entry = parseFloat(entryPrice);
  const high = parseFloat(asPerHigh);
  if (isNaN(entry) || isNaN(high) || entry === 0) return null;
  const isSell = ["sell", "short"].includes(signal?.toLowerCase());
  return isSell ? ((entry - high) / entry) * 100 : ((high - entry) / entry) * 100;
};

// Extracted so usePartnersWS hook can be called per row (hooks can't go inside .map())
const TradeRow = ({
  item,
  index,
  isEdit,
  showLtp,
  showClosedCols,
  showActions,
  showCancelReason,
  showCallType,
  selectedTradeType,
  expandedRows,
  toggleRow,
  handleEditBtn,
  handleCancelBtn,
  handleExitBtn,
  handleOpenRevisedModal,
}) => {
  console.log(item.ltp,"item")
  const wsSymbol = isFuturesType(selectedTradeType) ? item.fut_symbol : item.symbol_name;
  const { ltp: wsLtp } = usePartnersWS({ symbol: wsSymbol });
  const ltp = wsLtp ?? item.ltp;

  const livePnl = calcLivePnl({
    entryPrice: item.averaging || item.entry_price,
    ltp,
    quantity: item.quantity ?? 1,
    lotSize: item.lot_size ?? 1,
    signal: item.signal,
  });


  const isExpanded = !!expandedRows[item.id];
  const hasAccordion = selectedTradeType === "cash" && !!item.averaging;

  return (
    <React.Fragment>
      <tr
        onClick={hasAccordion ? () => toggleRow(item.id) : undefined}
        style={{ cursor: hasAccordion ? "pointer" : "default" }}
      >
        <td>{index + 1}</td>
        <td>{item.symbol_name}</td>
        <td>{item.segment}</td>
        <td>{item.signal?.toUpperCase()}</td>
        {showCallType && <td>{item?.call_type || "—"}</td>}
        {showCallType && <td>{item?.call_type === "BID" ? item?.bid_type || "—" : "—"}</td>}

        <td>{item.entry_price}</td>
        <td>{item.stop_loss}</td>

        {/* Live LTP — OPEN trades only, beside Stop Loss */}
        {showLtp && (
          <td style={{ whiteSpace: "nowrap" }}>
            {ltp != null ? (
              <span style={{ fontWeight: 700, color: "#016551" }}>{ltp}</span>
            ) : (
              <span style={{ color: "#94a3b8" }}>—</span>
            )}
          </td>
        )}

        {/* Live P&L amount — OPEN trades only */}
        {showLtp && (
          <td style={{ whiteSpace: "nowrap" }}>
            {livePnl != null ? (
              <span style={{ fontWeight: 700, color: livePnl.isProfit ? "#16a34a" : "#ef4444" }}>
                ₹{livePnl.pnl}
              </span>
            ) : (
              <span style={{ color: "#94a3b8" }}>—</span>
            )}
          </td>
        )}

        {/* Live P&L % — OPEN trades only */}
        {showLtp && (
          <td style={{ whiteSpace: "nowrap" }}>
            {livePnl != null ? (
              <span style={{ fontWeight: 700, color: livePnl.isProfit ? "#16a34a" : "#ef4444" }}>
                {livePnl.pnlPercent}%
              </span>
            ) : (
              <span style={{ color: "#94a3b8" }}>—</span>
            )}
          </td>
        )}

        {/* High After Entry — cash segment, OPEN trades only, Automation trades only */}
        {selectedTradeType === "cash" && showLtp && (
          <td>
            {item?.trade_type === "Automation" && item?.user === "Automation"
              ? item?.as_per_high ?? "—"
              : "—"}
          </td>
        )}
        {/* High After Entry % — same scope as High After Entry */}
        {selectedTradeType === "cash" && showLtp && (() => {
          const isAutomation = item?.trade_type === "Automation" && item?.user === "Automation";
          const pct = isAutomation
            ? calcHighAfterEntryPct(item?.entry_price, item?.as_per_high, item?.signal)
            : null;
          return (
            <td style={{ fontWeight: 600, color: pct != null ? (pct >= 0 ? "#16a34a" : "#ef4444") : undefined }}>
              {pct != null ? `${pct.toFixed(2)}%` : "—"}
            </td>
          );
        })()}
        {selectedTradeType === "cash" && (
          <td>{item?.valid_for_parrva ? "True" : "False"}</td>
        )}

        <td>{item.target_price}</td>
        {showClosedCols && <td>{item.exit_price}</td>}
        {showClosedCols && <td>{fmtDate(item?.exit_price_time)}</td>}

        <td>{fmtDate(item.created_at)}</td>
        {isFuturesType(selectedTradeType) && (
          <td>{item?.lot_size != null ? Math.round(Number(item.lot_size)) : "—"}</td>
        )}

        {showClosedCols && isFuturesType(selectedTradeType) && (
          <td>{item?.profit_loss != null ? `₹${item.profit_loss}` : "N/A"}</td>
        )}
        {showClosedCols && selectedTradeType === "cash" && (
          <td>
            {item?.profit_loss_percent
              ? `${item?.profit_loss_percent?.toFixed(2)}%`
              : "N/A"}
          </td>
        )}
        {showClosedCols && <td>{item?.trade_status || "-"}</td>}

        {isFuturesType(selectedTradeType) && (() => {
          const isShort = item?.signal?.toLowerCase() === "short";
          const price = isShort ? item?.as_per_low  : item?.as_per_high;
          const pl    = isShort ? item?.pl_as_per_low : item?.pl_as_per_high;
          return (
            <>
              <td>{price ?? "—"}</td>
              <td style={{ fontWeight: 600, color: pl != null ? (pl >= 0 ? "#16a34a" : "#ef4444") : undefined }}>
                {pl != null ? `₹${pl}` : "—"}
              </td>
            </>
          );
        })()}

        <td onClick={(e) => e.stopPropagation()}>
          {item?.file ? (
            <a
              className={styles.fileDownloadLink}
              href={item.file}
              target="_blank"
              rel="noreferrer"
              title={item?.file_name || "Download"}
            >
              <DownloadIcon sx={{ fontSize: 14 }} />
            </a>
          ) : (
            <span style={{ color: "#94a3b8", fontSize: 12 }}>—</span>
          )}
        </td>
        {selectedTradeType === "cash" && (
          <td>{item.averaging ?? "-"}</td>
        )}

        {!isEdit && showActions && (
          <td onClick={(e) => e.stopPropagation()}>
            <div className={styles.buttonWrapper}>
              <Tooltip title="Edit Trade" placement="top">
                <button
                  className={styles.editBtn}
                  onClick={() => handleEditBtn(item)}
                  disabled={
                    item?.trade_status === "CANCELLED" ||
                    item?.trade_status === "EXITED" ||
                    item?.trade_status === "CLOSED"
                  }
                >
                  <EditIcon sx={{ fontSize: 14 }} />
                </button>
              </Tooltip>
              <Tooltip title="Cancel Trade" placement="top">
                <button
                  className={`${styles.editBtn} ${styles.cancelBtn}`}
                  onClick={() => handleCancelBtn(item)}
                  disabled={
                    item?.trade_status === "CANCELLED" ||
                    item?.trade_status === "EXITED" ||
                    item?.trade_status === "CLOSED"
                  }
                >
                  <CancelIcon sx={{ fontSize: 14 }} />
                </button>
              </Tooltip>
              {selectedTradeType === "cash" && (
                <Tooltip title="Revise Entry Price" placement="top">
                  <button
                    className={`${styles.editBtn} ${styles.revisedBtn}`}
                    onClick={() => handleOpenRevisedModal(item)}
                    disabled={
                      item?.trade_status === "CANCELLED" ||
                      item?.trade_status === "EXITED" ||
                      item?.trade_status === "CLOSED"
                    }
                  >
                    <PriceChangeIcon sx={{ fontSize: 14 }} />
                  </button>
                </Tooltip>
              )}
              <Tooltip title="Exit Trade" placement="top">
                <button
                  className={`${styles.editBtn} ${styles.exitBtn}`}
                  onClick={() => handleExitBtn(item)}
                  disabled={
                    item?.trade_status === "CANCELLED" ||
                    item?.trade_status === "EXITED" ||
                    item?.trade_status === "CLOSED"
                  }
                >
                  <ExitToAppIcon sx={{ fontSize: 14 }} />
                </button>
              </Tooltip>
            </div>
          </td>
        )}

        {isFuturesType(selectedTradeType) && (
          <td>{item?.trade_type || "—"}</td>
        )}

        {selectedTradeType === "cash" && (
          <td style={{ textAlign: "center", width: 30 }}>
            {hasAccordion && (
              <span style={{ fontSize: 10, color: "#016551", fontWeight: 700 }}>
                {isExpanded ? "▲" : "▼"}
              </span>
            )}
          </td>
        )}
        <td>{item?.user || "—"}</td>
        {showCancelReason && (
          <td>
            {item?.cancel_reason ? (
              <Tooltip title={item.cancel_reason} placement="top">
                <span
                  style={{
                    maxWidth: "140px",
                    display: "inline-block",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    cursor: "pointer",
                  }}
                >
                  {item.cancel_reason.length > 20
                    ? item.cancel_reason.split("").splice(0, 20).join("") + "..."
                    : item.cancel_reason}
                </span>
              </Tooltip>
            ) : (
              "-"
            )}
          </td>
        )}
      </tr>

      {/* Accordion row — cash rows with averaging only */}
      {hasAccordion && isExpanded && (
        <tr>
          <td
            colSpan={99}
            style={{
              padding: 0,
              background: "#f0fdf4",
              borderBottom: "2px solid #bbf7d0",
            }}
          >
            <div
              style={{
                padding: "14px 28px",
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
              }}
            >
              <div
                style={{
                  background: "#04343a",
                  borderRadius: 8,
                  padding: "8px 16px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  minWidth: 90,
                }}
              >
                <span style={{ fontSize: 10, color: "#b8d4c4", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  Entry
                </span>
                <span style={{ fontSize: 13, fontWeight: 700, color: "#fff", marginTop: 2 }}>
                  ₹{item.entry_price}
                </span>
              </div>
              {item?.revised_entries?.length > 0 ? (
                <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0 }}>
                  {item.revised_entries.map((entry, i) => (
                    <React.Fragment key={entry.id}>
                      <span style={{ color: "#016551", fontSize: 16, fontWeight: 700, padding: "0 6px" }}>→</span>
                      <div
                        style={{
                          background: "#fff",
                          border: "1.5px solid #bbf7d0",
                          borderRadius: 8,
                          padding: "6px 14px",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          minWidth: 90,
                        }}
                      >
                        <span style={{ fontSize: 10, color: "#64748b", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                          Rev. {i + 1}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#016551", marginTop: 2 }}>
                          ₹{entry.revised_price}
                        </span>
                        <span style={{ fontSize: 10, color: "#94a3b8", marginTop: 1 }}>
                          {fmtDate(entry.created_at)}
                        </span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <span style={{ color: "#94a3b8", fontSize: 12, alignSelf: "center" }}>
                  No revised entries
                </span>
              )}
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
};

const TradeCallsTable = ({
  allTradesList,
  handleEditBtn,
  handleCancelBtn,
  handleExitBtn,
  isEdit,
  selectedTradeType,
  refreshTrades,
}) => {
  console.log(allTradesList, "allTradesList")
  const { user } = useSelector((state) => state.auth);
  const userId = user?.userDetails?.id;
  const [activeTab, setActiveTabState] = useState(
    () => sessionStorage.getItem("trade_active_tab") || "OPEN"
  );
  const setActiveTab = (tab) => {
    setActiveTabState(tab);
    sessionStorage.setItem("trade_active_tab", tab);
  };
  const tradeTabs = ["OPEN", "CLOSED", "INACTIVE", "CANCEL"];
  useEffect(() => {
    if (!tradeTabs.includes(activeTab)) setActiveTab("OPEN");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTradeType]);
  const [expandedRows, setExpandedRows] = useState({});
  const [autoTradeData, setAutoTradeData] = useState(null);
  const [autoTradeToggling, setAutoTradeToggling] = useState(null);

  useEffect(() => {
    fetchAutoTradeData();
  }, []);

  // Subscribe all OPEN trade symbols to WS when list changes
  useEffect(() => {
    const symbols = (allTradesList || [])
      .filter((item) => item?.trade_status?.toUpperCase() === "OPEN")
      .map((item) => selectedTradeType === "future" ? item.fut_symbol : item.symbol_name)
      .filter(Boolean);
    if (symbols.length > 0) partnersWSManager.bulkSubscribe(symbols);
  }, [allTradesList, selectedTradeType]);

  const fetchAutoTradeData = async () => {
    try {
      const res = await axiosConfig({
        method: "get",
        url: "predictions/premium-research-auto-trade-button/",
      });
      setAutoTradeData(res.data?.data ?? null);
    } catch (error) {
      console.error("Error fetching auto trade data:", error);
    }
  };

  const handleAutoTradeToggle = async (key) => {
    if (!autoTradeData || autoTradeToggling) return;
    const updated = { ...autoTradeData, [key]: !autoTradeData[key] };
    setAutoTradeToggling(key);
    try {
      const res = await axiosConfig({
        method: "post",
        url: "predictions/premium-research-auto-trade-button/",
        data: updated,
      });
      setAutoTradeData(res.data?.data ?? updated);
      toast.success(res.data?.message || "Settings updated successfully");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to update settings");
    } finally {
      setAutoTradeToggling(null);
    }
  };
  const toggleRow = (id) =>
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  const [revisedPriceModal, setRevisedPriceModal] = useState({
    open: false,
    item: null,
  });
  const [revisedPrice, setRevisedPrice] = useState("");
  const [revisedPriceLoading, setRevisedPriceLoading] = useState(false);

  const handleOpenRevisedModal = (item) => {
    setRevisedPrice("");
    setRevisedPriceModal({ open: true, item });
  };

  const handleCloseRevisedModal = () => {
    setRevisedPriceModal({ open: false, item: null });
    setRevisedPrice("");
  };

  const handleSubmitRevisedPrice = async () => {
    if (!revisedPrice) return;
    try {
      setRevisedPriceLoading(true);
      const response = await axiosConfig({
        method: "post",
        url: `/predictions/revised-cash-entry-price`,
        data: {
          call_id: revisedPriceModal.item?.id,
          revised_price: revisedPrice,
        },
      });
      console.log(response, "response");
      toast.success(
        response?.data?.response || "Entry price revised successfully",
      );
      handleCloseRevisedModal();
      refreshTrades();
    } catch (err) {
      const errMsg =
        err?.response?.data?.response ||
        err.message ||
        "Failed to revise entry price";
      toast.error(errMsg);
      console.error("Revised price error:", err);
    } finally {
      setRevisedPriceLoading(false);
    }
  };

  const filteredList = useMemo(() => {
    if (!allTradesList) return [];
    const field = activeTab === "CLOSED" ? "exit_price_time" : "created_at";
    return allTradesList
      .filter((item) => {
        const status = item?.trade_status?.toUpperCase();
        if (activeTab === "OPEN") return status === "OPEN";
        const isCancelled = status === "CANCEL" || status === "CANCELLED";
        if (activeTab === "CLOSED")
          return (
            status === "CLOSED" || status === "EXITED"
          );
        if (activeTab === "INACTIVE") return status === "INACTIVE";
        if (activeTab === "CANCEL") return isCancelled;
        return true;
      })
      .sort((a, b) => {
        const da = a[field] ? new Date(a[field]).getTime() : 0;
        const db = b[field] ? new Date(b[field]).getTime() : 0;
        return db - da;
      });
  }, [allTradesList, activeTab]);

  const [csvLoading, setCsvLoading] = useState(false);

  const downloadCSV = async () => {
    try {
      setCsvLoading(true);

      const [openRes, closeRes] = await Promise.all([
        axiosConfig({ method: "get", url: `/predictions/call/${userId}/${selectedTradeType}`, data: {} }),
        axiosConfig({ method: "get", url: getClosedTradesUrl(selectedTradeType), data: {} }),
      ]);

      console.log("[CSV] open response:", openRes?.data);
      console.log("[CSV] close response:", closeRes?.data);

      const extract = (res) => {
        const raw = res?.data?.response?.data || res?.data?.response || res?.data || [];
        return Array.isArray(raw) ? raw : [];
      };

      const list = [...extract(openRes), ...extract(closeRes)].sort((a, b) => {
        const da = a.created_at ? new Date(a.created_at).getTime() : 0;
        const db = b.created_at ? new Date(b.created_at).getTime() : 0;
        if (db !== da) return db - da;
        const ea = a.exit_price_time ? new Date(a.exit_price_time).getTime() : 0;
        const eb = b.exit_price_time ? new Date(b.exit_price_time).getTime() : 0;
        return eb - ea;
      });
      console.log("[CSV] total records:", list.length);

      const columns = [
        { label: "Sr No",          key: (_, i) => i + 1 },
        { label: "Symbol",         key: "symbol_name" },
        { label: "Fut Symbol",     key: "fut_symbol" },
        { label: "Segment",        key: "segment" },
        { label: "Signal",         key: "signal" },
        { label: "Entry Price",    key: "entry_price" },
        { label: "Averaging",      key: "averaging" },
        { label: "Target Price",   key: "target_price" },
        { label: "Exit Price",     key: "exit_price" },
        { label: "Stop Loss",      key: "stop_loss" },
        { label: "Lot Size",       key: "lot_size" },
        { label: "Quantity",       key: "quantity" },
        { label: "Trade Status",   key: "trade_status" },
        ...(selectedTradeType !== "cash" ? [{ label: "Profit/Loss", key: "profit_loss" }] : []),
        { label: "Profit/Loss %",  key: "profit_loss_percent" },
        { label: "Trade Type",     key: "trade_type" },
        { label: "Created At",     key: (item) => fmtDate(item.created_at) },
        { label: "Modified At",    key: (item) => fmtDate(item.modified_at) },
        { label: "Exit Date",      key: (item) => fmtDate(item.exit_price_time) },
        { label: "Cancel Reason",  key: "cancel_reason" },
        { label: "User",           key: "user" },
      ];

      const escape = (val) => {
        if (val == null || val === "") return "";
        const str = String(val);
        return str.includes(",") || str.includes('"') || str.includes("\n")
          ? `"${str.replace(/"/g, '""')}"`
          : str;
      };

      const header = columns.map((c) => c.label).join(",");
      const rows = list.map((item, i) =>
        columns
          .map((c) => escape(typeof c.key === "function" ? c.key(item, i) : item[c.key]))
          .join(",")
      );

      const csv = [header, ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = `trades_${selectedTradeType}_all_${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      toast.error("Failed to download CSV");
    } finally {
      setCsvLoading(false);
    }
  };

  return (
    <div className={styles.TradeDetailsTable}>
      {/* Section header */}
      <div
        style={{
          background: "#04343a",
          borderRadius: "12px",
          padding: "14px 20px",
          marginBottom: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
        }}
      >
        <span style={{ color: "#ffffff", fontWeight: 700, fontSize: 16 }}>
          Trades History
        </span>

        <div style={{ display: "flex", gap: 16, alignItems: "center", justifyContent: "flex-end" }}>
          {/* Auto Trade Status Buttons */}
          {autoTradeData && (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {[
                { label: "Cash Buy", key: "cash_Buy_enabled" },
                { label: "Cash Sell", key: "cash_Sell_enabled" },
                { label: "Future Buy", key: "future_Buy_enabled" },
                { label: "Future Sell", key: "future_Sell_enabled" },
              ].map(({ label, key }) => {
                const enabled = autoTradeData[key];
                return (
                  <div
                    key={key}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 3,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        color: "#fff",
                        fontWeight: 600,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {label}
                    </span>
                    <div
                      onClick={() => handleAutoTradeToggle(key)}
                      style={{
                        padding: "4px 14px",
                        borderRadius: 6,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: 0.5,
                        background: enabled
                          ? "linear-gradient(135deg, #1db954, #17a349)"
                          : "rgba(239,68,68,0.25)",
                        color: enabled ? "#fff" : "#f87171",
                        border: `1px solid ${enabled ? "rgba(29,185,84,0.5)" : "rgba(239,68,68,0.4)"}`,
                        boxShadow: enabled
                          ? "0 2px 8px rgba(29,185,84,0.3)"
                          : "none",
                        whiteSpace: "nowrap",
                        cursor: autoTradeToggling ? "not-allowed" : "pointer",
                        opacity: autoTradeToggling === key ? 0.6 : 1,
                        transition: "opacity 0.15s, background 0.2s",
                      }}
                    >
                      {autoTradeToggling === key
                        ? "..."
                        : enabled
                          ? "ON"
                          : "OFF"}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <div style={{ display: "flex", gap: "0" }}>
              {tradeTabs.map((tab, i) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: "7px 20px",
                    cursor: "pointer",
                    backgroundColor:
                      activeTab === tab ? "#016551" : "rgba(255,255,255,0.12)",
                    color: "#fff",
                    border: "none",
                    borderRadius:
                      i === 0 ? "6px 0 0 6px" : i === tradeTabs.length - 1 ? "0 6px 6px 0" : "0",
                    fontWeight: activeTab === tab ? 700 : 400,
                    fontSize: 13,
                    transition: "background 0.2s",
                  }}
                >
                  {tab.charAt(0) + tab.slice(1).toLowerCase()}
                </button>
              ))}
            </div>

            <button
              onClick={downloadCSV}
              disabled={csvLoading}
              title="Download full trades CSV"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "6px 12px",
                background: csvLoading ? "#64748b" : "#016551",
                color: "#fff",
                border: "none",
                borderRadius: 6,
                cursor: csvLoading ? "not-allowed" : "pointer",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <DownloadIcon sx={{ fontSize: 15 }} />
              {csvLoading ? "..." : "CSV"}
            </button>
          </div>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <table className={styles.tableContainer}>
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Symbol</th>
              <th>Segment</th>
              <th>Signal</th>
              {activeTab === "INACTIVE" && <th>Call Type</th>}
              {activeTab === "INACTIVE" && <th>Bid Type</th>}

              <th>Entry</th>
              <th>Stop Loss</th>
              {activeTab === "OPEN" && <th>LTP</th>}
              {activeTab === "OPEN" && <th>Live P&L</th>}
              {activeTab === "OPEN" && <th>Live P&L %</th>}
              {selectedTradeType === "cash" && activeTab === "OPEN" && <th>High After Entry</th>}
              {selectedTradeType === "cash" && activeTab === "OPEN" && <th>High After Entry %</th>}
              {selectedTradeType === "cash" && <th>Valid for Parrva</th>}
              <th>Target</th>
              {activeTab !== "OPEN" && <th>Exit</th>}
              {activeTab !== "OPEN" && <th>Exit Date</th>}

              <th>Created</th>
              {isFuturesType(selectedTradeType) && <th>Lot Size</th>}

              {activeTab !== "OPEN" && isFuturesType(selectedTradeType) && <th>Profit / Loss</th>}
              {activeTab !== "OPEN" && selectedTradeType === "cash" && <th>Profit / Loss %</th>}
              {activeTab !== "OPEN" && <th>Trade Status</th>}
              {isFuturesType(selectedTradeType) && <th>As Per High/Low</th>}
              {isFuturesType(selectedTradeType) && <th>P&L As Per High/Low</th>}

              <th>Download</th>
              {selectedTradeType === "cash" && <th>Average</th>}
              {!isEdit && (activeTab === "OPEN" || activeTab === "INACTIVE") && <th>Actions</th>}
              {isFuturesType(selectedTradeType) && <th>Trade Type</th>}
              {selectedTradeType === "cash" && <th></th>}
              <th>User</th>
              {(activeTab === "INACTIVE" || activeTab === "CANCEL") && <th>Cancel Reason</th>}
            </tr>
          </thead>

          <tbody className={styles.tableBodyContainer}>
            {filteredList?.map((item, index) => (
              <TradeRow
                key={item.id}
                item={item}
                index={index}
                isEdit={isEdit}
                showLtp={activeTab === "OPEN"}
                showClosedCols={activeTab !== "OPEN"}
                showActions={activeTab === "OPEN" || activeTab === "INACTIVE"}
                showCancelReason={activeTab === "INACTIVE" || activeTab === "CANCEL"}
                showCallType={activeTab === "INACTIVE"}
                selectedTradeType={selectedTradeType}
                expandedRows={expandedRows}
                toggleRow={toggleRow}
                handleEditBtn={handleEditBtn}
                handleCancelBtn={handleCancelBtn}
                handleExitBtn={handleExitBtn}
                handleOpenRevisedModal={handleOpenRevisedModal}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Revised Price Modal */}
      {revisedPriceModal.open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={handleCloseRevisedModal}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "12px",
              padding: "28px 32px",
              minWidth: 340,
              boxShadow: "0 8px 32px rgba(4,52,58,0.18)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                margin: "0 0 20px",
                color: "#04343a",
                fontSize: 16,
                fontWeight: 700,
              }}
            >
              Revise Entry Price
            </h3>

            {/* Symbol Name */}
            <div style={{ marginBottom: 14 }}>
              <label
                style={{
                  width: "100%",
                  fontSize: 14,
                  boxSizing: "border-box",
                  marginBottom: 4,
                }}

                // style={{
                //   fontSize: 12,
                //   color: "#e2e8f0",
                //   fontWeight: 600,
                //   display: "block",
                // }}
              >
                Symbol Name
              </label>
              <input
                value={revisedPriceModal.item?.symbol_name || ""}
                disabled
                style={{
                  width: "100%",
                  padding: "8px 6px",
                  border: "1px solid #cbd5e1",
                  fontSize: 14,
                  boxSizing: "border-box",
                  outline: "none",
                  marginBottom: 4,
                }}
              />
            </div>

            {/* Entry Price */}
            <div style={{ marginBottom: 14 }}>
              <label
                style={{
                  width: "100%",
                  borderRadius: 6,
                  fontSize: 14,
                  boxSizing: "border-box",
                }}
                // style={{
                //   fontSize: 12,
                //   color: "#e2e8f0",
                //   fontWeight: 600,
                //   display: "block",
                //   marginBottom: 4,
                // }}
              >
                Entry Price
              </label>
              <input
                value={revisedPriceModal.item?.entry_price || ""}
                disabled
                style={{
                  width: "100%",
                  padding: "8px 6px",

                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                  fontSize: 14,
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />
            </div>

            {/* Revised Price */}
            <div style={{ marginBottom: 24 }}>
              <label
                style={{
                  width: "100%",
                  borderRadius: 6,
                  fontSize: 14,
                  boxSizing: "border-box",
                }}
              >
                Revised Price
              </label>
              <input
                type="text"
                value={revisedPrice}
                onChange={(e) => setRevisedPrice(e.target.value)}
                placeholder="Enter revised price"
                style={{
                  width: "100%",
                  padding: "8px 6px",
                  borderRadius: 6,
                  border: "1px solid #cbd5e1",
                  fontSize: 14,
                  boxSizing: "border-box",
                  outline: "none",
                }}
              />
            </div>

            <div
              style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}
            >
              <button
                onClick={handleCloseRevisedModal}
                style={{
                  padding: "8px 20px",
                  borderRadius: 6,
                  border: "1px solid #e2e8f0",
                  background: "#fff",
                  color: "#64748b",
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitRevisedPrice}
                disabled={revisedPriceLoading || !revisedPrice}
                style={{
                  padding: "8px 20px",
                  borderRadius: 6,
                  border: "none",
                  background:
                    revisedPriceLoading || !revisedPrice
                      ? "#94a3b8"
                      : "#016551",
                  color: "#fff",
                  cursor:
                    revisedPriceLoading || !revisedPrice
                      ? "not-allowed"
                      : "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {revisedPriceLoading ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TradeCallsTable;
